// Small reusable field primitives. The onboarding form has a lot of
// inputs; centralising the Label+Input pattern keeps each section file
// short and consistent.
import React from 'react';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';

export function TextField({ label, value, onChange, placeholder, type = 'text', ...rest }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Input
        type={type}
        value={value ?? ''}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        {...rest}
      />
    </div>
  );
}

export function NumberField({ label, value, onChange, min, max, step = 1, ...rest }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Input
        type="number"
        value={value ?? ''}
        min={min}
        max={max}
        step={step}
        onChange={(e) => {
          const v = e.target.value;
          onChange(v === '' ? null : Number(v));
        }}
        {...rest}
      />
    </div>
  );
}

export function SelectField({ label, value, onChange, options, placeholder }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Select value={value ?? ''} onValueChange={onChange}>
        <SelectTrigger><SelectValue placeholder={placeholder || 'Select…'} /></SelectTrigger>
        <SelectContent>
          {options.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export function SwitchField({ label, description, value, onChange }) {
  return (
    <div className="flex items-start justify-between gap-4 py-1.5">
      <div>
        <Label className="leading-tight">{label}</Label>
        {description && (
          <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
        )}
      </div>
      <Switch checked={!!value} onCheckedChange={onChange} />
    </div>
  );
}

// Simple comma-separated list editor — callers keep the value as an array
// in state, we stringify/parse at the boundary.
export function ListField({ label, value, onChange, placeholder }) {
  const text = Array.isArray(value) ? value.join(', ') : '';
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Input
        value={text}
        placeholder={placeholder || 'comma, separated, list'}
        onChange={(e) => {
          const parts = e.target.value
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean);
          onChange(parts);
        }}
      />
    </div>
  );
}

// Multi-select for enum-like arrays: render each option as a toggle chip.
export function ChipMultiField({ label, value, onChange, options }) {
  const selected = new Set(value || []);
  const toggle = (val) => {
    const next = new Set(selected);
    if (next.has(val)) next.delete(val); else next.add(val);
    onChange(Array.from(next));
  };
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => {
          const isOn = selected.has(opt.value);
          return (
            <button
              type="button"
              key={opt.value}
              onClick={() => toggle(opt.value)}
              className={`text-xs px-2.5 py-1 rounded-full border transition ${
                isOn
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-background text-muted-foreground border-border hover:border-primary/50'
              }`}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
